<template>
    <div v-if="open" class="gallery-overlay" role="dialog" aria-modal="true" aria-label="Create email template">
        <div class="gallery-dialog">
            <header class="gallery-header">
                <div>
                    <h2>Create email template</h2>
                    <p>Pick a visual starting point — you can customize everything afterwards.</p>
                </div>
                <button type="button" class="icon-button" aria-label="Close" @click="$emit('close')">
                    <span class="material-icons">close</span>
                </button>
            </header>

            <div v-if="loading" class="gallery-state">
                <span class="spinner" aria-hidden="true"></span>
                <p>Loading starter templates…</p>
            </div>

            <div v-else-if="loadError" class="gallery-state error-state" role="alert">
                <span class="material-icons">error_outline</span>
                <p>{{ loadError }}</p>
                <button type="button" class="secondary-button" @click="loadStarters">Try again</button>
            </div>

            <template v-else>
                <div class="category-tabs" role="tablist" aria-label="Starter categories">
                    <button
                        v-for="tab in categoryTabs"
                        :key="tab.value"
                        type="button"
                        :class="['category-tab', { active: activeCategory === tab.value }]"
                        @click="activeCategory = tab.value"
                    >
                        {{ tab.label }}
                    </button>
                </div>

                <div class="starter-grid">
                    <article
                        v-for="starter in visibleStarters"
                        :key="starter.key"
                        :class="['starter-card', { selected: selectedKey === starter.key }]"
                    >
                        <div class="starter-preview" aria-hidden="true">
                            <iframe :srcdoc="starter.previewHtml" sandbox tabindex="-1" title=""></iframe>
                        </div>
                        <button type="button" class="starter-select" @click="selectStarter(starter)">
                            <span class="starter-title">
                                {{ starter.label }}
                                <span v-if="starter.category === 'marketing'" class="compliance-badge" title="Requires AGCO advertising review before publishing">
                                    Compliance
                                </span>
                            </span>
                            <span class="starter-desc">{{ starter.description }}</span>
                        </button>
                    </article>
                </div>

                <form class="create-form" @submit.prevent="createTemplate">
                    <label class="field duplicate-field">
                        <span>Or duplicate an existing template</span>
                        <select v-model="duplicateKey" @change="selectDuplicate">
                            <option value="">Use selected starter</option>
                            <option v-for="template in templates" :key="template.key" :value="template.key">
                                {{ template.label }}
                            </option>
                        </select>
                    </label>
                    <label class="field">
                        <span>Template name</span>
                        <input v-model="label" type="text" maxlength="120" required placeholder="e.g. Spring promo" />
                    </label>
                    <label class="field">
                        <span>Audience</span>
                        <input v-model="audience" type="text" maxlength="80" placeholder="e.g. Customers" />
                    </label>
                    <div class="form-actions">
                        <button type="button" class="secondary-button" @click="$emit('close')">Cancel</button>
                        <button type="submit" class="primary-button" :disabled="creating || !label.trim()">
                            <span v-if="creating" class="spinner inline" aria-hidden="true"></span>
                            {{ creating ? "Creating…" : "Create template" }}
                        </button>
                    </div>
                    <p v-if="formError" class="form-error" role="alert">{{ formError }}</p>
                </form>
            </template>
        </div>
    </div>
</template>

<script>
import EmailTemplateFetchApi from "../../utils/fetch/email-template-fetch-api"
import { errMsg } from "../../utils/common"

export default {
    name: "StarterGalleryModal",
    props: {
        open: { type: Boolean, default: false },
    },
    data() {
        return {
            starters: [],
            templates: [],
            loading: false,
            loadError: "",
            activeCategory: "all",
            selectedKey: "starter_blank",
            duplicateKey: "",
            label: "",
            audience: "",
            creating: false,
            formError: "",
        }
    },
    computed: {
        categoryTabs() {
            const tabs = [{ value: "all", label: "All" }]
            for (const category of ["transactional", "operational", "marketing"]) {
                if (this.starters.some((starter) => starter.category === category)) {
                    tabs.push({ value: category, label: this.categoryLabel(category) })
                }
            }
            return tabs
        },
        visibleStarters() {
            if (this.activeCategory === "all") return this.starters
            return this.starters.filter((starter) => starter.category === this.activeCategory)
        },
    },
    watch: {
        open(value) {
            if (value) {
                this.selectedKey = "starter_blank"
                this.duplicateKey = ""
                this.label = ""
                this.audience = ""
                this.formError = ""
                this.loadStarters()
            }
        },
    },
    methods: {
        async loadStarters() {
            this.loading = true
            this.loadError = ""
            try {
                const [starters, templates] = await Promise.all([
                    EmailTemplateFetchApi.listStarters(),
                    EmailTemplateFetchApi.list(),
                ])
                this.starters = starters
                this.templates = templates.filter((template) => !template.isArchived)
            } catch (err) {
                this.loadError = errMsg(err, "Starter templates could not be loaded.")
            }
            this.loading = false
        },
        selectStarter(starter) {
            this.selectedKey = starter.key
            this.duplicateKey = ""
            if (!this.label.trim()) {
                this.label = this.defaultLabel(starter)
            }
        },
        selectDuplicate() {
            if (!this.duplicateKey) return
            this.selectedKey = ""
            const template = this.templates.find((item) => item.key === this.duplicateKey)
            if (template && !this.label.trim()) this.label = `${template.label} variant`
        },
        defaultLabel(starter) {
            if (starter.key === "starter_blank") return ""
            return starter.label.replace(/\s*\(start with this design\)/i, "")
        },
        categoryLabel(category) {
            return category === "transactional"
                ? "Transactional"
                : category === "operational"
                    ? "Operational"
                    : "Marketing"
        },
        async createTemplate() {
            if (!this.label.trim() || this.creating) return
            this.creating = true
            this.formError = ""
            try {
                const template = await EmailTemplateFetchApi.create({
                    ...(this.duplicateKey
                        ? { duplicateKey: this.duplicateKey }
                        : { starterKey: this.selectedKey || "starter_blank" }),
                    label: this.label.trim(),
                    audience: this.audience.trim() || undefined,
                })
                this.$emit("created", template)
            } catch (err) {
                this.formError = errMsg(err, "Could not create the template.")
            } finally {
                this.creating = false
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.gallery-overlay {
    position: fixed;
    inset: 0;
    z-index: 60;
    display: grid;
    place-items: center;
    padding: 24px;
    background: rgba(14, 35, 30, 0.55);
}

.gallery-dialog {
    display: flex;
    width: min(880px, 100%);
    max-height: min(760px, 92vh);
    flex-direction: column;
    border-radius: 12px;
    background: #ffffff;
    color: #173b35;
    box-shadow: 0 24px 60px rgba(10, 30, 25, 0.35);
}

.gallery-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 22px 24px 16px;
    border-bottom: 1px solid #e4ebe8;
}

.gallery-header h2 {
    margin: 0;
    font-size: 21px;
    font-weight: 780;
}

.gallery-header p {
    margin: 5px 0 0;
    color: #65766f;
    font-size: 13px;
}

.icon-button {
    display: grid;
    width: 34px;
    height: 34px;
    place-items: center;
    border: 1px solid #d8e2de;
    border-radius: 7px;
    background: #fff;
    color: #5c716a;
    cursor: pointer;
}

.category-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 14px 24px 0;
}

.category-tab {
    padding: 6px 12px;
    border: 1px solid #d3deda;
    border-radius: 999px;
    background: #fff;
    color: #4f665e;
    font-size: 12px;
    font-weight: 750;
    cursor: pointer;
}

.category-tab.active {
    border-color: #123b34;
    background: #123b34;
    color: #fff;
}

.starter-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 10px;
    padding: 14px 24px;
    overflow-y: auto;
}

.starter-card {
    display: grid;
    overflow: hidden;
    border: 1px solid #dce5e1;
    border-radius: 9px;
    background: #fbfdfc;
    color: #173b35;
}

.starter-card:hover {
    border-color: #8faca3;
    background: #f3f8f6;
}

.starter-card.selected {
    border-color: #123b34;
    box-shadow: 0 0 0 2px rgba(18, 59, 52, 0.18);
}

.starter-preview {
    position: relative;
    height: 132px;
    overflow: hidden;
    border-bottom: 1px solid #e2e9e6;
    background: #eef3f1;
    pointer-events: none;
}

.starter-preview iframe {
    width: 720px;
    height: 440px;
    border: 0;
    transform: scale(0.34);
    transform-origin: top left;
}

.starter-select {
    display: flex;
    min-height: 92px;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding: 13px 14px;
    border: 0;
    background: transparent;
    color: inherit;
    text-align: left;
    cursor: pointer;
}

.starter-title {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 7px;
    font-size: 14px;
    font-weight: 780;
}

.starter-desc {
    color: #6b7c76;
    font-size: 12px;
    line-height: 1.4;
}

.compliance-badge {
    padding: 2px 6px;
    border-radius: 999px;
    background: #f7e9d8;
    color: #8a5c24;
    font-size: 9px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.create-form {
    display: grid;
    grid-template-columns: 1fr 1fr auto;
    align-items: end;
    gap: 12px;
    padding: 16px 24px 22px;
    border-top: 1px solid #e4ebe8;
    background: #f8faf9;
}

.duplicate-field {
    grid-column: 1 / -1;
}

.field {
    display: grid;
    gap: 6px;
}

.field span {
    color: #536961;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.6px;
    text-transform: uppercase;
}

.field input,
.field select {
    width: 100%;
    min-height: 42px;
    padding: 0 12px;
    border: 1px solid #cbd7d3;
    border-radius: 6px;
    background: #fff;
    color: #173b35;
    font-size: 14px;
}

.form-actions {
    display: flex;
    gap: 8px;
}

.primary-button,
.secondary-button {
    display: inline-flex;
    min-height: 42px;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 0 15px;
    border-radius: 7px;
    font-size: 13px;
    font-weight: 750;
    cursor: pointer;
}

.primary-button {
    border: 1px solid #123b34;
    background: #123b34;
    color: #fff;
}

.primary-button:disabled {
    cursor: not-allowed;
    opacity: 0.55;
}

.secondary-button {
    border: 1px solid #c9d6d1;
    background: #fff;
    color: #173b35;
}

.form-error {
    grid-column: 1 / -1;
    margin: 0;
    color: #a34b3e;
    font-size: 12px;
}

.gallery-state {
    display: grid;
    min-height: 260px;
    place-items: center;
    align-content: center;
    gap: 12px;
    padding: 24px;
    color: #65766f;
    font-size: 13px;
}

.gallery-state.error-state {
    color: #a34b3e;
}

.spinner {
    width: 28px;
    height: 28px;
    border: 3px solid #d5e1dc;
    border-top-color: #1e5a4c;
    border-radius: 50%;
    animation: spin 900ms linear infinite;
}

.spinner.inline {
    width: 14px;
    height: 14px;
    border-width: 2px;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}
</style>
